'use client';

import * as React from 'react';
import {createContext, useContext} from 'react';
import {CookieBanner, CookiePreferences} from './cookie-banner';
import {useCookieConsent} from '../../hooks/use-cookie-consent';

interface CookieBannerContextType {
  preferences: CookiePreferences | null;
  hasConsent: boolean;
  updatePreferences: (preferences: CookiePreferences) => void;
  clearPreferences: () => void;
  hasConsentFor: (category: keyof CookiePreferences) => boolean;
}

const CookieBannerContext = createContext<CookieBannerContextType | undefined>(
  undefined
);

export function useCookieBanner() {
  const context = useContext(CookieBannerContext);
  if (context === undefined) {
    throw new Error(
      'useCookieBanner must be used within a CookieBannerProvider'
    );
  }
  return context;
}

interface CookieBannerProviderProps {
  children: React.ReactNode;
  locale?: 'en' | 'no' | 'sv' | 'da';
  onAcceptAll?: (preferences: CookiePreferences) => void;
  onAcceptSelected?: (preferences: CookiePreferences) => void;
  onDecline?: () => void;
  showSettings?: boolean;
  position?: 'bottom' | 'top';
  title?: string;
  description?: string;
  acceptAllText?: string;
  acceptSelectedText?: string;
  declineText?: string;
  settingsText?: string;
  learnMoreText?: string;
  necessaryText?: string;
  analyticsText?: string;
  marketingText?: string;
  necessaryDescription?: string;
  analyticsDescription?: string;
  marketingDescription?: string;
  cookiePolicyUrl?: string;
}

export function CookieBannerProvider({
  children,
  locale = 'no',
  onAcceptAll,
  onAcceptSelected,
  onDecline,
  showSettings = true,
  position = 'bottom',
  title,
  description,
  acceptAllText,
  acceptSelectedText,
  declineText,
  settingsText,
  learnMoreText,
  necessaryText,
  analyticsText,
  marketingText,
  necessaryDescription,
  analyticsDescription,
  marketingDescription,
  cookiePolicyUrl
}: CookieBannerProviderProps) {
  const {
    preferences,
    hasConsent,
    updatePreferences,
    clearPreferences,
    hasConsentFor
  } = useCookieConsent();

  const handleAcceptAll = (preferences: CookiePreferences) => {
    updatePreferences(preferences);
    onAcceptAll?.(preferences);
  };

  const handleAcceptSelected = (preferences: CookiePreferences) => {
    updatePreferences(preferences);
    onAcceptSelected?.(preferences);
  };

  const handleDecline = () => {
    const declined: CookiePreferences = {
      necessary: true,
      analytics: false,
      marketing: false
    };
    updatePreferences(declined);
    onDecline?.();
  };

  const contextValue: CookieBannerContextType = {
    preferences,
    hasConsent,
    updatePreferences,
    clearPreferences,
    hasConsentFor
  };

  return (
    <CookieBannerContext.Provider value={contextValue}>
      {children}
      <CookieBanner
        locale={locale}
        onAcceptAll={handleAcceptAll}
        onAcceptSelected={handleAcceptSelected}
        onDecline={handleDecline}
        showSettings={showSettings}
        position={position}
        title={title}
        description={description}
        acceptAllText={acceptAllText}
        acceptSelectedText={acceptSelectedText}
        declineText={declineText}
        settingsText={settingsText}
        learnMoreText={learnMoreText}
        necessaryText={necessaryText}
        analyticsText={analyticsText}
        marketingText={marketingText}
        necessaryDescription={necessaryDescription}
        analyticsDescription={analyticsDescription}
        marketingDescription={marketingDescription}
        cookiePolicyUrl={cookiePolicyUrl}
        className="z-100"
      />
    </CookieBannerContext.Provider>
  );
}
